import React, { useMemo, useState } from 'react';
import {
  Button,
  Form,
  Input,
  InputNumber,
  Popconfirm,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  Typography,
  message,
} from 'antd';
import { DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons';
import type { ColumnsType, ColumnType } from 'antd/es/table';
import PageContainer from '../../components/common/PageContainer';
import { useTranslation } from '../../hooks/useTranslation';
import {
  StatusRecord,
  useCreateStatus,
  useDeleteStatus,
  useStatusEntities,
  useStatusList,
  useUpdateStatus,
} from '../../hooks/parameters/useStatus';
import {extractErrorMessage} from '../../utils/apiError';

type EditableStatus = StatusRecord & { id: string | number; __isNew?: boolean };

const NEW_ROW_ID = '__new__';
const ALL_ENTITIES = '__all__';

interface EditableCellProps extends React.HTMLAttributes<HTMLElement> {
  editing: boolean;
  dataIndex: keyof StatusRecord;
  title: string;
  inputType: 'text' | 'number' | 'switch' | 'select';
  record: EditableStatus;
  entityOptions: string[];
  children: React.ReactNode;
}

const EditableCell: React.FC<EditableCellProps> = ({
  editing,
  dataIndex,
  title,
  inputType,
  entityOptions,
  children,
  ...rest
}) => {
  const { t } = useTranslation();

  let input: React.ReactNode;
  switch (inputType) {
    case 'number':
      input = <InputNumber min={0} style={{ width: '100%' }} />;
      break;
    case 'switch':
      input = <Switch />;
      break;
    case 'select':
      input = (
        <Select
          showSearch
          allowClear={false}
          placeholder={title}
          options={entityOptions.map((value) => ({ label: value, value }))}
        />
      );
      break;
    default:
      input = <Input />;
  }

  const required = dataIndex === 'code' || dataIndex === 'name' || dataIndex === 'entity';
  const valuePropName = inputType === 'switch' ? 'checked' : 'value';

  return (
    <td {...rest}>
      {editing ? (
        <Form.Item
          name={dataIndex}
          valuePropName={valuePropName}
          style={{ margin: 0 }}
          rules={required ? [{ required: true, message: t('settings.content.statusIssues.messages.required') }] : []}
        >
          {input}
        </Form.Item>
      ) : (
        children
      )}
    </td>
  );
};

const Status: React.FC = () => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [messageApi, contextHolder] = message.useMessage();

  const [entityFilter, setEntityFilter] = useState<string>(ALL_ENTITIES);
  const [editingKey, setEditingKey] = useState<string | number>('');
  const [newRow, setNewRow] = useState<EditableStatus | null>(null);

  const queryEntity = entityFilter === ALL_ENTITIES ? undefined : entityFilter;
  const { data: statuses = [], isLoading } = useStatusList(queryEntity);
  const { data: entityList = [] } = useStatusEntities();

  const createMutation = useCreateStatus();
  const updateMutation = useUpdateStatus();
  const deleteMutation = useDeleteStatus();

  const entityOptions = useMemo(() => {
    const set = new Set<string>(entityList);
    statuses.forEach((s) => s.entity && set.add(s.entity));
    if (queryEntity) set.add(queryEntity);
    return Array.from(set).sort();
  }, [entityList, statuses, queryEntity]);

  const isEditing = (record: EditableStatus) => record.id === editingKey;

  const cancel = () => {
    setEditingKey('');
    setNewRow(null);
    form.resetFields();
  };

  const edit = (record: EditableStatus) => {
    form.setFieldsValue({
      code: record.code,
      name: record.name,
      description: record.description ?? '',
      entity: record.entity,
      sequence: record.sequence ?? 0,
      active: record.active ?? true,
    });
    setEditingKey(record.id);
  };

  const handleAdd = () => {
    if (editingKey) return;
    const defaultEntity = queryEntity || entityOptions[0] || '';
    const draft: EditableStatus = {
      id: NEW_ROW_ID,
      code: '',
      name: '',
      description: '',
      entity: defaultEntity,
      sequence: 0,
      active: true,
      __isNew: true,
    };
    setNewRow(draft);
    form.setFieldsValue(draft);
    setEditingKey(NEW_ROW_ID);
  };

  const save = async (record: EditableStatus) => {
    try {
      const row = (await form.validateFields()) as StatusRecord;
      const payload: StatusRecord = {
        code: row.code,
        name: row.name,
        description: row.description,
        entity: row.entity,
        sequence: row.sequence,
        active: row.active,
      };

      if (record.__isNew) {
        await createMutation.mutateAsync(payload);
        messageApi.success(t('settings.content.statusIssues.messages.created'));
      } else {
        await updateMutation.mutateAsync({ id: record.id, data: payload });
        messageApi.success(t('settings.content.statusIssues.messages.updated'));
      }
      cancel();
    } catch (err: any) {
      if (err?.errorFields) return;
      messageApi.error(extractErrorMessage(err, t('settings.content.statusIssues.messages.saveError')));
    }
  };

  const remove = async (record: EditableStatus) => {
    try {
      await deleteMutation.mutateAsync(record.id);
      messageApi.success(t('settings.content.statusIssues.messages.deleted'));
    } catch (error) {
      messageApi.error(extractErrorMessage(error, t('settings.content.statusIssues.messages.deleteError')));
    }
  };

  const dataSource: EditableStatus[] = useMemo(() => {
    const rows = (statuses as StatusRecord[])
      .filter((s) => !queryEntity || s.entity === queryEntity)
      .map((s) => ({ ...s, id: s.id ?? `${s.entity}-${s.code}` })) as EditableStatus[];
    return newRow ? [newRow, ...rows] : rows;
  }, [statuses, queryEntity, newRow]);

  type EditableColumn = ColumnType<EditableStatus> & {
    editable?: boolean;
    inputType?: 'text' | 'number' | 'switch' | 'select';
  };

  const columns: EditableColumn[] = [
    {
      title: t('settings.content.statusIssues.columns.code'),
      dataIndex: 'code',
      width: 140,
      editable: true,
      inputType: 'text',
    },
    {
      title: t('settings.content.statusIssues.columns.name'),
      dataIndex: 'name',
      editable: true,
      inputType: 'text',
    },
    {
      title: t('settings.content.statusIssues.columns.description'),
      dataIndex: 'description',
      editable: true,
      inputType: 'text',
      render: (value: string) => value || '-',
    },
    {
      title: t('settings.content.statusIssues.columns.entity'),
      dataIndex: 'entity',
      width: 160,
      editable: true,
      inputType: 'select',
      render: (value: string) => <Tag color="blue">{value}</Tag>,
    },
    {
      title: t('settings.content.statusIssues.columns.sequence'),
      dataIndex: 'sequence',
      width: 100,
      editable: true,
      inputType: 'number',
    },
    {
      title: t('settings.content.statusIssues.columns.active'),
      dataIndex: 'active',
      width: 100,
      editable: true,
      inputType: 'switch',
      render: (value: boolean) => (
        <Tag color={value ? 'success' : 'default'}>
          {value ? t('settings.content.statusIssues.columns.active') : '-'}
        </Tag>
      ),
    },
    {
      title: t('settings.content.statusIssues.columns.actions'),
      dataIndex: 'actions',
      width: 170,
      render: (_: unknown, record: EditableStatus) => {
        const editable = isEditing(record);
        const savePending = createMutation.isPending || updateMutation.isPending;
        return editable ? (
          <Space size="small">
            <Typography.Link onClick={() => save(record)} disabled={savePending}>
              {t('settings.content.statusIssues.buttons.save')}
            </Typography.Link>
            <Typography.Link onClick={cancel} disabled={savePending}>
              {t('settings.content.statusIssues.buttons.cancel')}
            </Typography.Link>
          </Space>
        ) : (
          <Space size="small">
            <Button
              type="link"
              size="small"
              icon={<EditOutlined />}
              disabled={!!editingKey}
              onClick={() => edit(record)}
            >
              {t('settings.content.statusIssues.buttons.edit')}
            </Button>
            <Popconfirm
              title={t('settings.content.statusIssues.confirm.deleteTitle')}
              description={t('settings.content.statusIssues.confirm.deleteDescription')}
              okText={t('settings.content.statusIssues.confirm.ok')}
              cancelText={t('settings.content.statusIssues.confirm.cancel')}
              okButtonProps={{ danger: true }}
              onConfirm={() => remove(record)}
              disabled={!!editingKey}
            >
              <Button
                type="link"
                size="small"
                danger
                icon={<DeleteOutlined />}
                disabled={!!editingKey}
              >
                {t('settings.content.statusIssues.buttons.delete')}
              </Button>
            </Popconfirm>
          </Space>
        );
      },
    },
  ];

  const mergedColumns = columns.map((col) => {
    if (!col.editable) return col;
    return {
      ...col,
      onCell: (record: EditableStatus) => ({
        record,
        inputType: col.inputType || 'text',
        dataIndex: col.dataIndex as keyof StatusRecord,
        title: col.title as string,
        editing: isEditing(record),
        entityOptions,
      }),
    };
  });

  return (
    <PageContainer
      title={t('settings.content.statusIssues.title')}
      subTitle={t('settings.content.statusIssues.description')}
      className="gt-py-x8 gt-px-x12"
      titleClassName="h3 gt-text-color-neutral-800"
      extra={
        <Button type="primary" icon={<PlusOutlined />} disabled={!!editingKey} onClick={handleAdd}>
          {t('settings.content.statusIssues.buttons.add')}
        </Button>
      }
    >
      {contextHolder}
      <Space style={{ marginBottom: 16 }} wrap>
        <span>{t('settings.content.statusIssues.filter.entity')}:</span>
        <Select
          style={{ minWidth: 240 }}
          value={entityFilter}
          onChange={(value) => {
            if (editingKey) cancel();
            setEntityFilter(value);
          }}
          placeholder={t('settings.content.statusIssues.filter.placeholder')}
          options={[
            { label: t('settings.content.statusIssues.filter.all'), value: ALL_ENTITIES },
            ...entityOptions.map((value) => ({ label: value, value })),
          ]}
        />
      </Space>

      <Form form={form} component={false}>
        <Table<EditableStatus>
          components={{ body: { cell: EditableCell } }}
          bordered
          size="small"
          rowKey="id"
          dataSource={dataSource}
          columns={mergedColumns as ColumnsType<EditableStatus>}
          loading={isLoading}
          pagination={{ pageSize: 10, showSizeChanger: true, pageSizeOptions: [10, 20, 50, 100] }}
          rowClassName="editable-row"
          locale={{ emptyText: t('settings.content.statusIssues.messages.empty') }}
        />
      </Form>
    </PageContainer>
  );
};

export default Status;
